import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { lookupAPI, referralAPI } from '../services/api';
import ContentHeader from '../components/ContentHeader';
import InfoIconButton from '../components/InfoIconButton';
import { formatDisplayDate } from '../utils/dateFormat';
import { formatTrackingMilestoneDisplayName } from '../utils/milestoneDisplayName';
import { hasAnyPermission, hasPermission } from '../utils/permissions';
import './ReferralMilestones.css';

/** True if the child's third birthday falls in May–August (extended services window). */
function isThirdBirthdayInExtendedServicesWindow(dateOfBirth) {
  if (!dateOfBirth) return false;
  const str = String(dateOfBirth);
  const ymd = str.includes('T') ? str.split('T')[0] : str;
  const [y, mo, d] = ymd.split('-').map(Number);
  if (!y || !mo || !d) return false;
  const third = new Date(y + 3, mo - 1, d);
  const month = third.getMonth() + 1;
  return month >= 5 && month <= 8;
}

function ReferralMilestones({ referralIdOverride, clientIdOverride }) {
  const IFSP_ANNUAL_TYPES = ['IFSP_12MO_1', 'IFSP_12MO_2'];
  const SCHOOL_TPC_TYPES = ['SCHOOL', 'TPC'];

  /** Options depend on milestone type (12-mo IFSP vs School/TPC). */
  const getSpecialCaseOptionsForType = (milestoneType) => {
    if (IFSP_ANNUAL_TYPES.includes(milestoneType)) {
      return [{ value: 'EXTENDED_TO_AGE_THREE', label: 'Extended to age three' }];
    }
    if (SCHOOL_TPC_TYPES.includes(milestoneType)) {
      return [
        { value: 'DECLINED', label: 'Declined' },
        { value: 'LT_90_DAYS_TO_AGE_3', label: '<90 days to age 3' },
      ];
    }
    return [];
  };

  const SPECIAL_CASE_TYPES = [...IFSP_ANNUAL_TYPES, ...SCHOOL_TPC_TYPES];
  const params = useParams();
  const location = useLocation();
  /** Only `/referral/:id/milestones` uses :id as referral id. On `/client/:id/milestones`, :id is the client. */
  const referralIdFromReferralRoute = useMemo(() => {
    const p = location.pathname || '';
    if (!/\/referral\/[^/]+\/milestones\/?$/.test(p)) return undefined;
    return params.id ?? undefined;
  }, [location.pathname, params.id]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantPath } = useTenantFromUrl();
  const clientId = clientIdOverride ?? location.state?.clientId ?? null;

  const [data, setData] = useState({ referral: null, client: null, milestones: [] });
  const [clientReferrals, setClientReferrals] = useState([]);
  const queryReferralIdRaw = searchParams.get('referralId');
  const resolvedClientId = clientIdOverride ?? data.client?.id ?? null;

  useEffect(() => {
    if (!resolvedClientId) return;
    let cancelled = false;
    referralAPI
      .getByClientId(resolvedClientId)
      .then((res) => {
        if (!cancelled) setClientReferrals(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setClientReferrals([]);
      });
    return () => {
      cancelled = true;
    };
  }, [resolvedClientId]);

  const effectiveReferralId = useMemo(() => {
    if (referralIdFromReferralRoute) return referralIdFromReferralRoute;
    if (queryReferralIdRaw) {
      if (clientReferrals.length === 0) return queryReferralIdRaw;
      const m = clientReferrals.find((r) => String(r.id) === String(queryReferralIdRaw));
      return m ? m.id : referralIdOverride ?? null;
    }
    return referralIdOverride ?? null;
  }, [referralIdFromReferralRoute, queryReferralIdRaw, clientReferrals, referralIdOverride]);

  /** Drop invalid ?referralId= once list is loaded (embedded client route only). */
  useEffect(() => {
    if (!queryReferralIdRaw || clientReferrals.length === 0 || referralIdFromReferralRoute) return;
    const ok = clientReferrals.some((r) => String(r.id) === String(queryReferralIdRaw));
    if (!ok && resolvedClientId) {
      navigate(tenantPath(`/client/${resolvedClientId}/milestones`), { replace: true });
    }
  }, [
    queryReferralIdRaw,
    clientReferrals,
    referralIdFromReferralRoute,
    resolvedClientId,
    navigate,
    tenantPath,
  ]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tpcLateReasons, setTpcLateReasons] = useState([]);
  const [savingCompletedId, setSavingCompletedId] = useState(null);
  /** Local value while user is typing in a completed-date input; save happens on blur so keyboard entry isn't reset */
  const [editingCompletedDate, setEditingCompletedDate] = useState({});
  const [trackingAlertStatuses, setTrackingAlertStatuses] = useState([]);
  const [savingTrackingStatusId, setSavingTrackingStatusId] = useState(null);
  /** User chose Confirmed but must pick meeting date before PATCH (required together). */
  const [awaitingConfirmedMeeting, setAwaitingConfirmedMeeting] = useState(null);
  const [confirmedMeetingDateDraft, setConfirmedMeetingDateDraft] = useState({});
  const [selectedTpcLateReasonId, setSelectedTpcLateReasonId] = useState('');
  const [savingTpcLateReason, setSavingTpcLateReason] = useState(false);
  const [savingEiesTracking, setSavingEiesTracking] = useState(false);
  const [savingExtendedDates, setSavingExtendedDates] = useState(false);
  const [annual1ExtendedDate, setAnnual1ExtendedDate] = useState('');
  const [annual2ExtendedDate, setAnnual2ExtendedDate] = useState('');
  const [annualExtendedHintOpen, setAnnualExtendedHintOpen] = useState(false);
  /** Row expand (Option A): Earliest, Latest, workflow Status, Special case; annual extended on 12‑mo IFSP rows */
  const [expandedMilestoneIds, setExpandedMilestoneIds] = useState(() => new Set());
  /** Show full EIES form when 3rd birthday outside May–Aug (edge cases) */
  const [eiesShowOverride, setEiesShowOverride] = useState(false);
  /** After saving a completion date, optional prompt to open Client Email with a suggested template */
  const [emailSuggestPrompt, setEmailSuggestPrompt] = useState(null);
  const [eiesForm, setEiesForm] = useState({
    es_interested: '',
    sped_eligible_date: '',
    sped_ineligible_date: '',
    es_accepted: '',
    ei_es_na_august_bday: '',
    parent_delay_ec_determination_date: '',
    lea_delay_ec_determination_date: '',
    parent_dispute_ec_determ: '',
    parent_decline_ec_determ: '',
    esy_accepted: '',
    ei_planned_closure_date: '',
  });

  const loadMilestones = useCallback(async () => {
    if (!effectiveReferralId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await referralAPI.getMilestones(effectiveReferralId);
      const payload = res.data?.data || {};
      setData({
        referral: payload.referral || null,
        client: payload.client || null,
        milestones: payload.milestones || [],
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load tracking');
      setData({ referral: null, client: null, milestones: [] });
    } finally {
      setLoading(false);
    }
  }, [effectiveReferralId]);

  useEffect(() => {
    if (!effectiveReferralId) {
      setLoading(false);
      setError('No referral selected for tracking.');
      setData({ referral: null, client: null, milestones: [] });
      return;
    }
    loadMilestones();
  }, [effectiveReferralId, loadMilestones]);

  useEffect(() => {
    lookupAPI.getTpcLateReasons()
      .then((res) => setTpcLateReasons(res.data?.data || []))
      .catch(() => setTpcLateReasons([]));
  }, []);

  useEffect(() => {
    lookupAPI.getTrackingAlertStatuses()
      .then((res) => setTrackingAlertStatuses(res.data?.data || []))
      .catch(() => setTrackingAlertStatuses([]));
  }, []);

  useEffect(() => {
    setSelectedTpcLateReasonId(data.referral?.tpc_late_reason_id || '');
  }, [data.referral?.tpc_late_reason_id]);

  /** Clear in-progress completed-date edits when milestones are refetched */
  useEffect(() => {
    setEditingCompletedDate({});
    setConfirmedMeetingDateDraft({});
    setAwaitingConfirmedMeeting(null);
  }, [data.milestones]);

  const confirmedTrackingStatus = useMemo(
    () => trackingAlertStatuses.find((s) => (s.code || '').toLowerCase() === 'confirmed') || null,
    [trackingAlertStatuses]
  );

  const isMilestoneConfirmed = (m) =>
    !!(confirmedTrackingStatus && String(m.tracking_alert_status_id || '') === String(confirmedTrackingStatus.id));

  useEffect(() => {
    setAnnual1ExtendedDate(toDateInputValue(data.referral?.annual_1_extended_date));
    setAnnual2ExtendedDate(toDateInputValue(data.referral?.annual_2_extended_date));
  }, [data.referral?.annual_1_extended_date, data.referral?.annual_2_extended_date]);

  const eiesEligible = useMemo(
    () => isThirdBirthdayInExtendedServicesWindow(data.client?.date_of_birth),
    [data.client?.date_of_birth]
  );
  const showEiesForm = eiesEligible || eiesShowOverride;

  const toggleMilestoneExpanded = useCallback((milestoneId) => {
    setExpandedMilestoneIds((prev) => {
      const next = new Set(prev);
      if (next.has(milestoneId)) next.delete(milestoneId);
      else next.add(milestoneId);
      return next;
    });
  }, []);

  const toBoolSelectValue = (value) => {
    if (value === true) return 'true';
    if (value === false) return 'false';
    return '';
  };

  const fromBoolSelectValue = (value) => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
  };

  const toDateInputValue = (value) => {
    if (!value) return '';
    const str = String(value);
    return str.includes('T') ? str.split('T')[0] : str;
  };

  useEffect(() => {
    const tracking = data.referral?.eies_tracking || {};
    setEiesForm({
      es_interested: toBoolSelectValue(tracking.es_interested),
      sped_eligible_date: toDateInputValue(tracking.sped_eligible_date),
      sped_ineligible_date: toDateInputValue(tracking.sped_ineligible_date),
      es_accepted: toBoolSelectValue(tracking.es_accepted),
      ei_es_na_august_bday: toBoolSelectValue(tracking.ei_es_na_august_bday),
      parent_delay_ec_determination_date: toDateInputValue(tracking.parent_delay_ec_determination_date),
      lea_delay_ec_determination_date: toDateInputValue(tracking.lea_delay_ec_determination_date),
      parent_dispute_ec_determ: toBoolSelectValue(tracking.parent_dispute_ec_determ),
      parent_decline_ec_determ: toBoolSelectValue(tracking.parent_decline_ec_determ),
      esy_accepted: toBoolSelectValue(tracking.esy_accepted),
      ei_planned_closure_date: toDateInputValue(tracking.ei_planned_closure_date),
    });
  }, [data.referral?.eies_tracking]);

  const formatDate = (dateString) => (dateString ? formatDisplayDate(dateString) : '—');

  const parseDate = (value) => {
    if (!value) return null;
    const d = new Date(`${String(value).split('T')[0]}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  /** Abbreviate milestone name for display so column doesn't wrap (e.g. 6-Month → 6-Mo) */
  const displayMilestoneName = (name) => {
    if (!name || typeof name !== 'string') return name;
    return name
      .replace(/\b6-Month\b/g, '6-Mo')
      .replace(/\b12-Month\b/g, '12-Mo');
  };

  /** Permission to edit; closed referrals are read-only (historical view). */
  const canEditTracking = hasAnyPermission('referrals.update', 'referrals.update_tracking');
  const isArchivedReferral = !!data.referral?.close_date;
  const canEditThisTracking = canEditTracking && !isArchivedReferral;

  const saveCompletedDate = async (milestoneId, value) => {
    if (!canEditThisTracking) return;
    const dateValue = (value && String(value).trim()) || null;
    const effectiveClientId = clientId ?? data.client?.id ?? null;
    try {
      setSavingCompletedId(milestoneId);
      const res = await referralAPI.updateMilestoneCompletedDate(effectiveReferralId, milestoneId, dateValue);
      const suggestions = res.data?.automation?.emailSuggestions || [];
      await loadMilestones();
      if (suggestions.length > 0 && effectiveClientId) {
        setEmailSuggestPrompt({ suggestions, clientId: effectiveClientId });
      }
    } catch (err) {
      console.error('Error updating completion date:', err);
      alert(err.response?.data?.error || 'Failed to update completion date');
    } finally {
      setSavingCompletedId(null);
    }
  };

  const saveSpecialCaseCode = async (milestoneId, specialCaseCode) => {
    if (!canEditThisTracking) return;
    try {
      setSavingCompletedId(milestoneId);
      await referralAPI.updateMilestoneTrackingStatus(effectiveReferralId, milestoneId, {
        special_case_code: specialCaseCode || null,
      });
      await loadMilestones();
    } catch (err) {
      console.error('Error updating special case:', err);
      alert(err.response?.data?.error || 'Failed to update special case');
    } finally {
      setSavingCompletedId(null);
    }
  };

  const handleCompletedDateBlur = (milestoneId, currentValue) => {
    const serverValue = data.milestones?.find((m) => m.id === milestoneId);
    const serverStr = serverValue ? toDateInputValue(serverValue.completed_date) : '';
    if (currentValue === serverStr) {
      setEditingCompletedDate((prev) => {
        const next = { ...prev };
        delete next[milestoneId];
        return next;
      });
      return;
    }
    setEditingCompletedDate((prev) => {
      const next = { ...prev };
      delete next[milestoneId];
      return next;
    });
    saveCompletedDate(milestoneId, currentValue);
  };

  /** Display order: Intake → ASQ → Initial IFSP → 6/12-Mo #1/#2/#3 → School → TPC → TPM → Exit */
  const MILESTONE_DISPLAY_ORDER = [
    'INTAKE',
    'ASQ',
    'IFSP_INITIAL',
    'IFSP_6MO_1',
    'IFSP_12MO_1',
    'IFSP_6MO_2',
    'IFSP_12MO_2',
    'IFSP_6MO_3',
    'SCHOOL',
    'TPC',
    'TPM',
    'EXIT',
  ];

  /** Recurring IFSP types (6-Mo, 12-Mo): hide when pending and no due date yet */
  const IFSP_RECURRING_TYPES = ['IFSP_6MO_1', 'IFSP_6MO_2', 'IFSP_6MO_3', 'IFSP_12MO_1', 'IFSP_12MO_2'];
  const isRecurringIfsp = (type) => IFSP_RECURRING_TYPES.includes(type);

  /** IFSP + Exit: no meaningful earliest/latest window in the UI — same as IFSP, show "NA". */
  const IFSP_TYPES = ['IFSP_INITIAL', ...IFSP_RECURRING_TYPES];
  const isIfspMilestone = (type) => IFSP_TYPES.includes(type);
  const noEarliestLatestWindow = (type) => isIfspMilestone(type) || type === 'EXIT';

  const sortedMilestones = [...(data.milestones || [])].sort((a, b) => {
    const ia = MILESTONE_DISPLAY_ORDER.indexOf(a.milestone_type);
    const ib = MILESTONE_DISPLAY_ORDER.indexOf(b.milestone_type);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  /** Hide pending 6-Mo/12-Mo IFSP milestones that have no due date yet */
  const visibleMilestones = sortedMilestones.filter((m) => {
    if ((m.status || '').toLowerCase() === 'completed') return true;
    if (isRecurringIfsp(m.milestone_type) && !m.due_date) return false;
    return true;
  });

  /** For IFSP and Exit, Earliest/Latest are N/A; show "NA" instead of a date. */
  const formatEarliestLatest = (dateString, milestoneType) =>
    noEarliestLatestWindow(milestoneType) ? 'NA' : formatDate(dateString);

  const tpcMilestone = visibleMilestones.find((m) => m.milestone_type === 'TPC') || null;
  const tpcCompletedDate = parseDate(tpcMilestone?.completed_date);
  const tpcLatestDate = parseDate(tpcMilestone?.latest_date);
  const tpcIsLate = !!(tpcCompletedDate && tpcLatestDate && tpcCompletedDate > tpcLatestDate);

  const handleTrackingStatusChange = async (milestoneId, trackingAlertStatusId) => {
    if (!canEditThisTracking) return;
    const value = trackingAlertStatusId || null;
    if (awaitingConfirmedMeeting && awaitingConfirmedMeeting.milestoneId !== milestoneId) {
      setAwaitingConfirmedMeeting(null);
      setConfirmedMeetingDateDraft((d) => {
        const next = { ...d };
        delete next[awaitingConfirmedMeeting.milestoneId];
        return next;
      });
    }
    const isPickingConfirmed =
      confirmedTrackingStatus && value && String(value) === String(confirmedTrackingStatus.id);
    if (isPickingConfirmed) {
      setAwaitingConfirmedMeeting({ milestoneId, statusId: value });
      setExpandedMilestoneIds((prev) => new Set(prev).add(milestoneId));
      return;
    }
    if (awaitingConfirmedMeeting?.milestoneId === milestoneId) {
      setAwaitingConfirmedMeeting(null);
      setConfirmedMeetingDateDraft((d) => {
        const next = { ...d };
        delete next[milestoneId];
        return next;
      });
    }
    try {
      setSavingTrackingStatusId(milestoneId);
      await referralAPI.updateMilestoneTrackingStatus(effectiveReferralId, milestoneId, {
        tracking_alert_status_id: value,
      });
      await loadMilestones();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update tracking status');
    } finally {
      setSavingTrackingStatusId(null);
    }
  };

  const saveConfirmedMeetingWithStatus = async (milestoneId) => {
    if (!canEditThisTracking || !effectiveReferralId) return;
    const pending = awaitingConfirmedMeeting;
    if (!pending || pending.milestoneId !== milestoneId) return;
    const raw =
      confirmedMeetingDateDraft[milestoneId] !== undefined
        ? confirmedMeetingDateDraft[milestoneId]
        : '';
    const dateStr = (raw || '').trim();
    if (!dateStr) {
      alert('Confirmed meeting date is required when status is Confirmed.');
      return;
    }
    try {
      setSavingTrackingStatusId(milestoneId);
      await referralAPI.updateMilestoneTrackingStatus(effectiveReferralId, milestoneId, {
        tracking_alert_status_id: pending.statusId,
        confirmed_meeting_date: dateStr,
      });
      setAwaitingConfirmedMeeting(null);
      setConfirmedMeetingDateDraft((d) => {
        const next = { ...d };
        delete next[milestoneId];
        return next;
      });
      await loadMilestones();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save confirmed meeting');
    } finally {
      setSavingTrackingStatusId(null);
    }
  };

  const handleConfirmedMeetingDateBlur = async (milestoneId, dateValue) => {
    if (!canEditThisTracking || !effectiveReferralId) return;
    if (awaitingConfirmedMeeting?.milestoneId === milestoneId) return;
    const row = data.milestones.find((x) => x.id === milestoneId);
    if (!row || !isMilestoneConfirmed(row)) return;
    const normalized = (dateValue || '').trim();
    if (!normalized) {
      alert('A confirmed meeting date is required while status is Confirmed. Change status to clear it.');
      await loadMilestones();
      return;
    }
    try {
      setSavingTrackingStatusId(milestoneId);
      await referralAPI.updateMilestoneTrackingStatus(effectiveReferralId, milestoneId, {
        confirmed_meeting_date: normalized,
      });
      await loadMilestones();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update confirmed meeting date');
    } finally {
      setSavingTrackingStatusId(null);
    }
  };

  const handleSaveTpcLateReason = async () => {
    if (!effectiveReferralId || !canEditThisTracking) return;
    try {
      setSavingTpcLateReason(true);
      await referralAPI.update(effectiveReferralId, {
        tpc_late_reason_id: selectedTpcLateReasonId || null,
      });
      await loadMilestones();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save TPC Late Reason');
    } finally {
      setSavingTpcLateReason(false);
    }
  };

  const handleEiesChange = (field, value) => {
    setEiesForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveExtendedDates = async () => {
    if (!effectiveReferralId || !canEditThisTracking) return;
    try {
      setSavingExtendedDates(true);
      await referralAPI.patchReferralTracking(effectiveReferralId, {
        annual_1_extended_date: annual1ExtendedDate || null,
        annual_2_extended_date: annual2ExtendedDate || null,
      });
      await loadMilestones();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save extended dates');
    } finally {
      setSavingExtendedDates(false);
    }
  };

  const handleSaveEiesTracking = async () => {
    if (!effectiveReferralId || !canEditThisTracking) return;
    try {
      setSavingEiesTracking(true);
      await referralAPI.patchReferralTracking(effectiveReferralId, {
        eies_tracking: {
          es_interested: fromBoolSelectValue(eiesForm.es_interested),
          sped_eligible_date: eiesForm.sped_eligible_date || null,
          sped_ineligible_date: eiesForm.sped_ineligible_date || null,
          es_accepted: fromBoolSelectValue(eiesForm.es_accepted),
          ei_es_na_august_bday: fromBoolSelectValue(eiesForm.ei_es_na_august_bday),
          parent_delay_ec_determination_date: eiesForm.parent_delay_ec_determination_date || null,
          lea_delay_ec_determination_date: eiesForm.lea_delay_ec_determination_date || null,
          parent_dispute_ec_determ: fromBoolSelectValue(eiesForm.parent_dispute_ec_determ),
          parent_decline_ec_determ: fromBoolSelectValue(eiesForm.parent_decline_ec_determ),
          esy_accepted: fromBoolSelectValue(eiesForm.esy_accepted),
          ei_planned_closure_date: eiesForm.ei_planned_closure_date || null,
        },
      });
      await loadMilestones();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save EIES tracking');
    } finally {
      setSavingEiesTracking(false);
    }
  };

  const milestonesHeader = (
    <ContentHeader
      title="Client Tracking"
    />
  );

  const formatReferralEpisodeOption = (r) =>
    `Opened ${formatDate(r.open_date)}${r.close_date ? ` · Closed ${formatDate(r.close_date)}` : ' · Open referral'}`;

  if (loading) {
    return (
      <div className="referral-milestones-page">
        {milestonesHeader}
        <p className="loading-msg">Loading tracking…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="referral-milestones-page">
        {milestonesHeader}
        <p className="error-msg">{error}</p>
      </div>
    );
  }

  const { referral, client } = data;
  const clientTag = client
    ? `${client.last_name || ''}, ${client.first_name || ''}`.trim() || 'Client'
    : null;
  const clientDob = client?.date_of_birth ? formatDate(client.date_of_birth) : null;

  return (
    <div className="referral-milestones-page">
      {milestonesHeader}

      {resolvedClientId && referral && (
        <div className="referral-milestones-episode-bar">
          <span className="referral-milestones-episode-summary">
            {referral.open_date != null ? (
              <>
                <strong>Tracking for</strong> {formatDate(referral.open_date)}
                {referral.close_date ? (
                  <> — Closed {formatDate(referral.close_date)}</>
                ) : (
                  <> — Open referral</>
                )}
              </>
            ) : (
              <>
                <strong>Tracking for</strong> —
              </>
            )}
          </span>
          {clientReferrals.length > 1 && (
            <label className="referral-milestones-episode-switch">
              <span className="referral-milestones-episode-switch-prefix" aria-hidden="true">
                Other referrals
              </span>
              <span className="visually-hidden">Change referral</span>
              <select
                value={String(effectiveReferralId)}
                onChange={(e) => {
                  const newId = e.target.value;
                  if (referralIdFromReferralRoute) {
                    navigate(tenantPath(`/referral/${newId}/milestones`), { replace: true });
                  } else {
                    navigate(
                      tenantPath(`/client/${resolvedClientId}/milestones?referralId=${encodeURIComponent(newId)}`),
                      { replace: true }
                    );
                  }
                }}
                aria-label="Change referral for client tracking"
                className="referral-milestones-episode-select"
              >
                {clientReferrals.map((r) => (
                  <option key={r.id} value={r.id}>
                    {formatReferralEpisodeOption(r)}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}


      {((!clientId && clientTag) ||
        !!(referral?.referral_level_1_description || referral?.referral_level_2_description)) && (
        <section className="referral-context">
          {!clientId && clientTag && (
            <p className="referral-context-line referral-context-primary">
              <strong>Client:</strong> {clientTag}
              {clientDob && <> · <strong>DOB:</strong> {clientDob}</>}
            </p>
          )}
          {(referral?.referral_level_1_description || referral?.referral_level_2_description) && (
            <p className="referral-context-line referral-context-secondary">
              <strong>Source:</strong> {[referral.referral_level_1_description, referral.referral_level_2_description].filter(Boolean).join(' / ')}
            </p>
          )}
        </section>
      )}

      {!canEditTracking && (
        <p className="milestones-readonly-hint" role="status">
          You can view this page, but you don&apos;t have permission to edit tracking fields. Ask an admin to grant{' '}
          <strong>Update Referral Tracking (Client Tracking)</strong> or <strong>Update Referrals</strong>.
        </p>
      )}
      {canEditTracking && isArchivedReferral && (
        <p className="milestones-archive-hint" role="status">
          This referral is closed. Client tracking for this episode is read-only.
        </p>
      )}

      <section className="milestones-section">
        {visibleMilestones.length === 0 ? (
          <p className="no-milestones">No tracking items recorded for this referral.</p>
        ) : (
          <div className="milestones-table-wrap">
            <table className="milestones-table milestones-table--compact-main">
              <thead>
                <tr>
                  <th className="milestone-expand-header" scope="col">
                    <span className="visually-hidden">Details</span>
                  </th>
                  <th>Tracking item</th>
                  <th>Due</th>
                  <th>Tracking status</th>
                  <th>Completed</th>
                </tr>
              </thead>
              <tbody>
                {visibleMilestones.flatMap((m) => {
                  const isCompleted = (m.status || '').toLowerCase() === 'completed';
                  const supportsSpecialCase = SPECIAL_CASE_TYPES.includes(m.milestone_type);
                  const completedDateValue = toDateInputValue(m.completed_date);
                  const showSpecialCaseSelect = supportsSpecialCase && (!!m.special_case_code || !completedDateValue);
                  const milestoneRowLabel = displayMilestoneName(
                    formatTrackingMilestoneDisplayName({
                      milestone_type: m.milestone_type,
                      milestone_name: m.milestone_name,
                      school_district_code: client?.school_district_code,
                    })
                  );
                  const isOpen = expandedMilestoneIds.has(m.id);
                  const rows = [];
                  rows.push(
                    <tr key={`${m.id}-main`} className={isCompleted ? 'milestone-completed' : ''}>
                      <td className="milestone-expand-cell">
                        <button
                          type="button"
                          className="milestone-expand-btn"
                          onClick={() => toggleMilestoneExpanded(m.id)}
                          aria-expanded={isOpen}
                          aria-controls={`milestone-detail-${m.id}`}
                          aria-label={isOpen ? `Hide details for ${milestoneRowLabel}` : `Show details for ${milestoneRowLabel}`}
                        >
                          {isOpen ? '−' : '+'}
                        </button>
                      </td>
                      <td className="milestone-name">{milestoneRowLabel}</td>
                      <td>{formatDate(m.due_date)}</td>
                      <td className="milestone-tracking-status-cell">
                        <select
                          aria-label={`Tracking status for ${milestoneRowLabel}`}
                          value={
                            awaitingConfirmedMeeting?.milestoneId === m.id
                              ? awaitingConfirmedMeeting.statusId
                              : (m.tracking_alert_status_id || '')
                          }
                          onChange={(e) => handleTrackingStatusChange(m.id, e.target.value || null)}
                          disabled={!canEditThisTracking || !!savingTrackingStatusId}
                          className="milestone-tracking-status-select"
                        >
                          <option value="">—</option>
                          {trackingAlertStatuses.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                        {savingTrackingStatusId === m.id && (
                          <span className="milestone-tracking-status-saving" aria-hidden="true">Saving…</span>
                        )}
                      </td>
                      <td className="milestone-completed-cell">
                        <input
                          type="date"
                          aria-label={`Completion date for ${milestoneRowLabel}`}
                          value={editingCompletedDate[m.id] !== undefined ? editingCompletedDate[m.id] : completedDateValue}
                          onChange={(e) => setEditingCompletedDate((prev) => ({ ...prev, [m.id]: e.target.value }))}
                          onBlur={(e) => handleCompletedDateBlur(m.id, e.target.value)}
                          disabled={!canEditThisTracking || !!savingCompletedId || !!m.special_case_code}
                          className="milestone-completed-date-input"
                        />
                        {savingCompletedId === m.id && (
                          <span className="milestone-completed-saving" aria-hidden="true">Saving…</span>
                        )}
                      </td>
                    </tr>
                  );
                  if (m.milestone_type === 'TPC' && tpcIsLate) {
                    rows.push(
                      <tr key={`${m.id}-tpc-late`} className="milestone-tpc-late-row">
                        <td colSpan={5}>
                          <div className="milestone-tpc-late-inline">
                            <span className="milestone-tpc-late-label">
                              <strong>TPC late reason</strong>
                              <span className="milestone-tpc-late-hint"> (only when TPC is completed after the latest allowed date)</span>
                            </span>
                            <div className="tpc-late-reason-row">
                              <select
                                id={`tpc_late_reason_id_${m.id}`}
                                aria-label="TPC Late Reason"
                                value={selectedTpcLateReasonId}
                                onChange={(e) => setSelectedTpcLateReasonId(e.target.value)}
                                disabled={!canEditThisTracking || savingTpcLateReason || !tpcIsLate}
                              >
                                <option value="">Select...</option>
                                {tpcLateReasons.map((reason) => (
                                  <option key={reason.id} value={reason.id}>
                                    {reason.code} - {reason.description}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="btn-primary"
                                onClick={handleSaveTpcLateReason}
                                disabled={!canEditThisTracking || savingTpcLateReason || !tpcIsLate}
                              >
                                {savingTpcLateReason ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  if (isOpen) {
                    rows.push(
                      <tr key={`${m.id}-detail`} className="milestone-detail-row">
                        <td colSpan={5}>
                          <div
                            className="milestone-detail-panel"
                            id={`milestone-detail-${m.id}`}
                            role="region"
                            aria-label={`Details for ${milestoneRowLabel}`}
                          >
                            <div
                              className={
                                confirmedTrackingStatus &&
                                (isMilestoneConfirmed(m) || awaitingConfirmedMeeting?.milestoneId === m.id)
                                  ? 'milestone-detail-grid milestone-detail-grid--with-confirmed'
                                  : 'milestone-detail-grid'
                              }
                            >
                              <div>
                                <div className="milestone-detail-label">Earliest</div>
                                <div>{formatEarliestLatest(m.earliest_date, m.milestone_type)}</div>
                              </div>
                              <div>
                                <div className="milestone-detail-label">Latest</div>
                                <div>{formatEarliestLatest(m.latest_date, m.milestone_type)}</div>
                              </div>
                              <div>
                                <div className="milestone-detail-label">Status</div>
                                <div>{m.status || '—'}</div>
                              </div>
                              {confirmedTrackingStatus &&
                                (isMilestoneConfirmed(m) || awaitingConfirmedMeeting?.milestoneId === m.id) && (
                                <div className="milestone-detail-confirmed-meeting-cell">
                                  <div className="milestone-detail-label">Confirmed meeting date</div>
                                  <div className="milestone-confirmed-meeting-row">
                                    <input
                                      type="date"
                                      aria-label={`Confirmed meeting date for ${milestoneRowLabel}`}
                                      value={
                                        confirmedMeetingDateDraft[m.id] !== undefined
                                          ? confirmedMeetingDateDraft[m.id]
                                          : toDateInputValue(m.confirmed_meeting_date)
                                      }
                                      onChange={(e) =>
                                        setConfirmedMeetingDateDraft((prev) => ({
                                          ...prev,
                                          [m.id]: e.target.value,
                                        }))
                                      }
                                      onBlur={(e) => {
                                        if (awaitingConfirmedMeeting?.milestoneId === m.id) return;
                                        const v =
                                          confirmedMeetingDateDraft[m.id] !== undefined
                                            ? confirmedMeetingDateDraft[m.id]
                                            : e.target.value;
                                        const server = toDateInputValue(m.confirmed_meeting_date);
                                        if ((v || '') === (server || '')) return;
                                        handleConfirmedMeetingDateBlur(m.id, v);
                                      }}
                                      disabled={!canEditThisTracking || !!savingTrackingStatusId}
                                      className="milestone-confirmed-meeting-date-input"
                                      required
                                    />
                                    {awaitingConfirmedMeeting?.milestoneId === m.id && (
                                      <button
                                        type="button"
                                        className="btn-primary milestone-confirmed-meeting-save"
                                        onClick={() => saveConfirmedMeetingWithStatus(m.id)}
                                        disabled={!canEditThisTracking || !!savingTrackingStatusId}
                                      >
                                        Save
                                      </button>
                                    )}
                                  </div>
                                  {awaitingConfirmedMeeting?.milestoneId === m.id && (
                                    <p className="milestone-detail-hint milestone-detail-hint--inline">
                                      Choose the meeting date, then Save, to apply Confirmed status.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                            {showSpecialCaseSelect && (
                              <div className="milestone-detail-special-case">
                                <label className="milestone-detail-label" htmlFor={`special_case_${m.id}`}>Special case</label>
                                <select
                                  id={`special_case_${m.id}`}
                                  aria-label={`Special case for ${milestoneRowLabel}`}
                                  value={m.special_case_code || ''}
                                  onChange={(e) => saveSpecialCaseCode(m.id, e.target.value)}
                                  disabled={!canEditThisTracking || !!savingCompletedId}
                                  className="milestone-special-case-select"
                                >
                                  <option value="">Not special</option>
                                  {getSpecialCaseOptionsForType(m.milestone_type).map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                            {m.milestone_type === 'IFSP_12MO_1' && (
                              <div className="milestone-annual-extended-block">
                                <div className="referral-milestones-section-title-with-hint milestone-annual-extended-title">
                                  <strong>Annual 1 extended date</strong>
                                  <InfoIconButton
                                    ariaExpanded={annualExtendedHintOpen}
                                    onClick={() => setAnnualExtendedHintOpen((prev) => !prev)}
                                    ariaLabel="Show explanation for annual extended date"
                                    ariaLabelExpanded="Hide explanation"
                                  />
                                </div>
                                {annualExtendedHintOpen && (
                                  <p className="annual-extended-hint">State-approved extensions for 12‑month IFSP #1 and #2 due dates. When set, the due date for that annual IFSP tracking item uses this date instead of the computed date.</p>
                                )}
                                <div className="extended-dates-row milestone-annual-extended-inputs">
                                  <div className="form-group">
                                    <label htmlFor="annual_1_extended_date">Date</label>
                                    <input
                                      id="annual_1_extended_date"
                                      type="date"
                                      value={annual1ExtendedDate}
                                      onChange={(e) => setAnnual1ExtendedDate(e.target.value)}
                                      disabled={!canEditThisTracking || savingExtendedDates}
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={handleSaveExtendedDates}
                                    disabled={!canEditThisTracking || savingExtendedDates}
                                  >
                                    {savingExtendedDates ? 'Saving...' : 'Save extended dates'}
                                  </button>
                                </div>
                              </div>
                            )}
                            {m.milestone_type === 'IFSP_12MO_2' && (
                              <div className="milestone-annual-extended-block">
                                <div className="referral-milestones-section-title-with-hint milestone-annual-extended-title">
                                  <strong>Annual 2 extended date</strong>
                                  <InfoIconButton
                                    ariaExpanded={annualExtendedHintOpen}
                                    onClick={() => setAnnualExtendedHintOpen((prev) => !prev)}
                                    ariaLabel="Show explanation for annual extended date"
                                    ariaLabelExpanded="Hide explanation"
                                  />
                                </div>
                                {annualExtendedHintOpen && (
                                  <p className="annual-extended-hint">State-approved extensions for 12‑month IFSP #1 and #2 due dates. When set, the due date for that annual IFSP tracking item uses this date instead of the computed date.</p>
                                )}
                                <div className="extended-dates-row milestone-annual-extended-inputs">
                                  <div className="form-group">
                                    <label htmlFor="annual_2_extended_date">Date</label>
                                    <input
                                      id="annual_2_extended_date"
                                      type="date"
                                      value={annual2ExtendedDate}
                                      onChange={(e) => setAnnual2ExtendedDate(e.target.value)}
                                      disabled={!canEditThisTracking || savingExtendedDates}
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={handleSaveExtendedDates}
                                    disabled={!canEditThisTracking || savingExtendedDates}
                                  >
                                    {savingExtendedDates ? 'Saving...' : 'Save extended dates'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  return rows;
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="milestones-section milestones-section--eies">
        <h2>EIES / Extended Services</h2>
        {!showEiesForm ? (
          <p className="eies-collapsed-hint">
            This section usually applies when the child&apos;s third birthday falls between May 1 and August 31 (extended services window).{' '}
            <button
              type="button"
              className="eies-show-override-btn"
              onClick={() => setEiesShowOverride(true)}
              disabled={!canEditThisTracking}
            >
              Show form anyway
            </button>
          </p>
        ) : (
          <>
            {!eiesEligible && (
              <p className="eies-override-banner">
                Showing the full form even though the third birthday is outside May–August.{' '}
                <button
                  type="button"
                  className="eies-show-override-btn"
                  onClick={() => setEiesShowOverride(false)}
                  disabled={!canEditThisTracking}
                >
                  Hide form
                </button>
              </p>
            )}
            <div className="eies-grid">
          <div className="form-group">
            <label htmlFor="eies_es_interested">ES Interested</label>
            <select
              id="eies_es_interested"
              value={eiesForm.es_interested}
              onChange={(e) => handleEiesChange('es_interested', e.target.value)}
              disabled={!canEditThisTracking || savingEiesTracking}
            >
              <option value="">Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="eies_sped_eligible_date">SpEd Eligible</label>
            <input
              id="eies_sped_eligible_date"
              type="date"
              value={eiesForm.sped_eligible_date}
              onChange={(e) => handleEiesChange('sped_eligible_date', e.target.value)}
              disabled={!canEditThisTracking || savingEiesTracking}
            />
          </div>

          <div className="form-group">
            <label htmlFor="eies_sped_ineligible_date">SpEd InEligible</label>
            <input
              id="eies_sped_ineligible_date"
              type="date"
              value={eiesForm.sped_ineligible_date}
              onChange={(e) => handleEiesChange('sped_ineligible_date', e.target.value)}
              disabled={!canEditThisTracking || savingEiesTracking}
            />
          </div>

          <div className="form-group">
            <label htmlFor="eies_es_accepted">ES Accepted</label>
            <select
              id="eies_es_accepted"
              value={eiesForm.es_accepted}
              onChange={(e) => handleEiesChange('es_accepted', e.target.value)}
              disabled={!canEditThisTracking || savingEiesTracking}
            >
              <option value="">Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="eies_ei_es_na_august_bday">EI-ES N/A August Bday</label>
            <select
              id="eies_ei_es_na_august_bday"
              value={eiesForm.ei_es_na_august_bday}
              onChange={(e) => handleEiesChange('ei_es_na_august_bday', e.target.value)}
              disabled={!canEditThisTracking || savingEiesTracking}
            >
              <option value="">Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="eies_parent_delay_ec_determination_date">Parent Delay EC Determ</label>
            <input
              id="eies_parent_delay_ec_determination_date"
              type="date"
              value={eiesForm.parent_delay_ec_determination_date}
              onChange={(e) => handleEiesChange('parent_delay_ec_determination_date', e.target.value)}
              disabled={!canEditThisTracking || savingEiesTracking}
            />
          </div>

          <div className="form-group">
            <label htmlFor="eies_lea_delay_ec_determination_date">LEA Delay EC Determ</label>
            <input
              id="eies_lea_delay_ec_determination_date"
              type="date"
              value={eiesForm.lea_delay_ec_determination_date}
              onChange={(e) => handleEiesChange('lea_delay_ec_determination_date', e.target.value)}
              disabled={!canEditThisTracking || savingEiesTracking}
            />
          </div>

          <div className="form-group">
            <label htmlFor="eies_parent_dispute_ec_determ">Parent Dispute EC Determ</label>
            <select
              id="eies_parent_dispute_ec_determ"
              value={eiesForm.parent_dispute_ec_determ}
              onChange={(e) => handleEiesChange('parent_dispute_ec_determ', e.target.value)}
              disabled={!canEditThisTracking || savingEiesTracking}
            >
              <option value="">Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="eies_parent_decline_ec_determ">Parent Decline EC Determ</label>
            <select
              id="eies_parent_decline_ec_determ"
              value={eiesForm.parent_decline_ec_determ}
              onChange={(e) => handleEiesChange('parent_decline_ec_determ', e.target.value)}
              disabled={!canEditThisTracking || savingEiesTracking}
            >
              <option value="">Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="eies_esy_accepted">ESY Accepted</label>
            <select
              id="eies_esy_accepted"
              value={eiesForm.esy_accepted}
              onChange={(e) => handleEiesChange('esy_accepted', e.target.value)}
              disabled={!canEditThisTracking || savingEiesTracking}
            >
              <option value="">Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="eies_ei_planned_closure_date">EI Planned Closure</label>
            <input
              id="eies_ei_planned_closure_date"
              type="date"
              value={eiesForm.ei_planned_closure_date}
              onChange={(e) => handleEiesChange('ei_planned_closure_date', e.target.value)}
              disabled={!canEditThisTracking || savingEiesTracking}
            />
          </div>
        </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={handleSaveEiesTracking}
                disabled={!canEditThisTracking || savingEiesTracking}
              >
                {savingEiesTracking ? 'Saving...' : 'Save EIES Tracking'}
              </button>
            </div>
          </>
        )}
      </section>

      {emailSuggestPrompt && (
        <div
          className="referral-milestones-modal-backdrop"
          role="presentation"
          onClick={() => setEmailSuggestPrompt(null)}
        >
          <div
            className="referral-milestones-modal"
            role="dialog"
            aria-labelledby="email-suggest-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="email-suggest-dialog-title" className="referral-milestones-modal-title">
              Preview email?
            </h2>
            <p className="referral-milestones-modal-body">
              A suggested letter is available
              {emailSuggestPrompt.suggestions[0]?.displayLabel
                ? `: ${emailSuggestPrompt.suggestions[0].displayLabel}`
                : emailSuggestPrompt.suggestions[0]?.templateBase
                  ? ` (${emailSuggestPrompt.suggestions[0].templateBase})`
                  : ''}
              .
              {emailSuggestPrompt.suggestions.length > 1 &&
                ` ${emailSuggestPrompt.suggestions.length - 1} more suggestion(s) are configured — preview uses the first.`}
            </p>
            <div className="referral-milestones-modal-actions">
              <button
                type="button"
                className="referral-milestones-modal-btn-cancel"
                onClick={() => setEmailSuggestPrompt(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="referral-milestones-modal-btn-primary"
                onClick={() => {
                  const first = emailSuggestPrompt.suggestions[0];
                  const base = first?.templateBase || '';
                  const cid = emailSuggestPrompt.clientId;
                  setEmailSuggestPrompt(null);
                  if (base && cid) {
                    navigate(
                      `${tenantPath(`/client/${cid}/email`)}?suggestBase=${encodeURIComponent(base)}`
                    );
                  }
                }}
              >
                Preview email
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default ReferralMilestones;
